"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Loader, MessageSquare } from "lucide-react";
function SidebarFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center h-full space-y-4 text-center p-6">
        <div className="size-12 rounded-lg bg-green-100 flex items-center justify-center mb-4">
          <MessageSquare className="size-6 text-green-600" />
        </div>
        <h3 className="font-default text-xl font-semibold text-foreground">Thank You!</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-md">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4 mt-6">
      <FormField control={control} name="name" rules={{
      required: "Name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <Input placeholder="NAME & SURNAME" className="border-dashed border-muted-foreground/50 bg-transparent rounded-md focus-visible:ring-1 focus-visible:ring-ring" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <Input type="email" placeholder="EMAIL ADDRESS" className="border-dashed border-muted-foreground/50 bg-transparent rounded-md focus-visible:ring-1 focus-visible:ring-ring" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormControl>
              <Input type="tel" placeholder="CONTACT NUMBER" className="border-dashed border-muted-foreground/50 bg-transparent rounded-md focus-visible:ring-1 focus-visible:ring-ring" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <Textarea placeholder="MESSAGE" className="min-h-[150px] border-dashed border-muted-foreground/50 bg-transparent rounded-md focus-visible:ring-1 focus-visible:ring-ring resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <div className="pt-4 flex justify-end">
        <Button type="submit" disabled={isSubmitting} className="bg-[var(--chart-4)] text-black hover:bg-[var(--chart-4)]/90 rounded-md px-8 font-semibold tracking-wide">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              SENDING...
            </> : 'SUBMIT'}
        </Button>
      </div>
    </Form>;
}
export default function ContactFormSidebar() {
  return <section id="contact-form-sidebar" className="py-16 bg-background flex flex-col items-center justify-center min-h-[400px] border-y border-border">
      <div className="text-center max-w-2xl mx-auto px-4 mb-8">
        <h2 className="font-default text-3xl font-bold mb-4 text-foreground">Get in Touch</h2>
        <p className="text-muted-foreground">
          Have a question or need assistance? Open our contact form to send us a message directly. Our team will get back to you as soon as possible.
        </p>
      </div>

      <Sheet>
        <SheetTrigger asChild>
          <Button size="lg" className="rounded-lg bg-[var(--chart-4)] text-black hover:bg-[var(--chart-4)]/90 font-bold text-lg px-8 py-6 shadow-md">
            <MessageSquare className="mr-2 size-5" />
            Enquiry
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="w-full sm:max-w-md bg-background border-l border-border p-6 sm:p-8 overflow-y-auto">
          <SheetHeader className="mb-6 text-left px-0">
            <SheetTitle className="font-default text-2xl font-bold text-foreground">Contact Us</SheetTitle>
            <SheetDescription className="text-muted-foreground">
              Fill out the form below and we'll get back to you shortly.
            </SheetDescription>
          </SheetHeader>
          
          <FormProvider formId="sidebar-contact-form" sectionName="Contact Form Sidebar" defaultValues={{
          name: "",
          email: "",
          phone: "",
          message: ""
        }} submitText="SUBMIT" successMessage="Your message has been sent successfully. We will contact you soon.">
            <SidebarFormContent />
          </FormProvider>
        </SheetContent>
      </Sheet>
    </section>;
}